import React from "react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export const PrivacyPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  const lastUpdated = new Date().toLocaleDateString();

  return (
    <main className="min-h-screen pt-24 bg-background px-6 py-16 max-w-6xl mx-auto text-foreground flex gap-10">
      {/* SEO META TAGS */}
      <title>The Blind Date — Privacy Policy & Terms</title>
      <meta
        name="description"
        content="Read the Privacy Policy and Terms & Conditions for The Blind Date to understand how your data is used and your rights as a user."
      />
      <meta name="robots" content="index, follow" />

      {/* SIDEBAR NAVIGATION */}
      <aside className="w-64 hidden md:block sticky top-20 h-fit bg-white/40 backdrop-blur-lg border border-border rounded-xl p-6 shadow-sm">
        <h3 className="text-xl font-semibold mb-4 text-primary">Navigation</h3>
        <ul className="space-y-3 text-foreground/70">
          <li>
            <a href="#privacy" className="hover:text-primary">
              Privacy Policy
            </a>
          </li>
          <li>
            <a href="#terms" className="hover:text-primary">
              Terms & Conditions
            </a>
          </li>
        </ul>
      </aside>

      {/* MAIN CONTENT */}
      <motion.div
        className="flex-1"
        variants={container}
        initial="hidden"
        animate="visible"
      >
        {/* BREADCRUMBS */}
        <motion.p variants={item} className="text-sm text-foreground/60 mb-6">
          <Link to="/" className="hover:text-primary">
            Home
          </Link>{" "}
          / Legal
        </motion.p>

        {/* PAGE TITLE */}
        <motion.h1
          variants={item}
          className="text-5xl font-light italic text-primary mb-10"
        >
          Legal Information
        </motion.h1>

        {/* PRIVACY POLICY */}
        <section id="privacy" className="mb-20">
          <motion.h2
            variants={item}
            className="text-3xl font-semibold text-primary mb-4"
          >
            Privacy Policy
          </motion.h2>

          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            Your privacy is extremely important to us. This Privacy Policy
            explains how The Blind Date collects, uses, and protects your
            personal data.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            1. Information We Collect
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70"
          >
            <li>Name or preferred name</li>
            <li>Age (18+ confirmation)</li>
            <li>Phone number and contact details</li>
            <li>Dating preferences and compatibility details</li>
            <li>Communication records (WhatsApp, email, website forms)</li>
            <li>
              Payment confirmation details (excluding full card or bank
              information)
            </li>
            <li>Blind date questionnaire responses used for matchmaking.</li>
            <li>Basic device/browser analytics for security.</li>
          </motion.ul>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-6 mb-6 leading-relaxed"
          >
            We do not intentionally collect sensitive personal data beyond what
            is necessary for facilitation.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            2. Purpose of Data Collection
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-2 mb-4 leading-relaxed"
          >
            Your information is collected solely to:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>Facilitate introductions and matches</li>
            <li>Communicate regarding services, schedules, and updates</li>
            <li>Process payments and confirmations</li>
            <li>Improve user experience and internal operations</li>
            <li>Address complaints or support requests</li>
          </motion.ul>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            We do not sell, rent, or trade your personal data.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            3. Consent
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-2 mb-4 leading-relaxed"
          >
            By using the Platform and submitting your information, you:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70"
          >
            <li>Confirm that you are 18 years or older</li>
            <li>
              Consent to the collection and use of your information as outlined
              in this Policy
            </li>
            <li>
              Consent to communication via WhatsApp, phone, or email for
              service-related purposes
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            4. Data Sharing & Disclosure
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-2 mb-4 leading-relaxed"
          >
            We do not share your personal information with third parties except:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>With your consent, where required for facilitation</li>
            <li>
              With venue partners, limited only to what is necessary (e.g., name
              for reservations, if applicable)
            </li>
            <li>
              When required by law, legal process, or government authority
            </li>
          </motion.ul>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            We are not responsible for how third-party venues handle information
            shared directly with them.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            5. Internal Access & Data Handling
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70"
          >
            <li>
              Access to personal data is restricted to authorised team members
              only
            </li>
            <li>
              Matchmaking team members access only what is necessary for
              facilitation
            </li>
            <li>Team members are bound by confidentiality obligations</li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            6. Data Storage & Security
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>
              We take reasonable steps to protect personal information from
              unauthorised access, misuse, or disclosure
            </li>
            <li>Data may be stored digitally on secure devices or platforms</li>
            <li>
              While we strive to protect data, no method of transmission or
              storage is 100% secure
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            7. Data Retention
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            Personal information is retained only for as long as necessary to
            provide services or meet legal requirements.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            8. User Rights
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-2 mb-4 leading-relaxed"
          >
            Subject to applicable law, users may:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>Request access to their personal information</li>
            <li>Request correction or deletion of data</li>
            <li>Withdraw consent for future communication</li>
          </motion.ul>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            Requests can be made via the Platform's contact details.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            9. Third-Party Links & Platforms
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>
              The Platform may link to or operate through third-party services
              (e.g., payment gateways, venues, social platforms)
            </li>
            <li>
              We are not responsible for the privacy practices of third parties
            </li>
            <li>
              Users are encouraged to review third-party privacy policies
              independently
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            10. Children's Privacy
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70"
          >
            <li>The Platform is strictly for users 18 years and above</li>
            <li>We do not knowingly collect data from minors</li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            11. Changes to This Privacy Policy
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            We reserve the right to update this Privacy Policy at any time.
            Continued use of the Platform after changes constitutes acceptance
            of the updated Policy.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            12. Contact
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            For questions, concerns, or data-related requests, please contact us
            via the details provided on the Platform.
          </motion.p>
        </section>

        {/* TERMS & CONDITIONS */}
        <section id="terms" className="mb-20">
          <motion.h2
            variants={item}
            className="text-3xl font-semibold text-primary mb-4"
          >
            Terms & Conditions
          </motion.h2>

          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            By accessing or using this website and our services you agree to be
            bound by the following Terms & Conditions.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            1. Nature of the Platform
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            The Platform operates as an introduction and facilitation service
            that connects individuals for social interactions. The Platform does
            not guarantee compatibility, chemistry, relationships, outcomes,
            safety, or conduct of any kind.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            2. Eligibility & User Representations
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-2 mb-4 leading-relaxed"
          >
            By using the Platform, you confirm that:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>You must be 18 years of age or older to use the Platform.</li>
            <li>All information provided is true and accurate</li>
            <li>You are legally capable of consenting</li>
            <li>
              The Platform reserves the right to reject, suspend, or remove any
              user at its discretion.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            3. Role of the Platform & Team
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>The Platform facilitates introductions only.</li>
            <li>
              Any communication by team members is informational and
              facilitative in nature.
            </li>
            <li>
              No team member has the authority to make guarantees,
              representations, refunds, or legally binding promises beyond what
              is stated in these Terms.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            4. User Responsibility & Assumption of Risk
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>
              All meetings, interactions, and communications occur at the user's
              own discretion and risk.
            </li>
            <li>
              Users are solely responsible for their conduct before, during, and
              after any meeting.
            </li>
            <li>
              The Platform shall not be liable for behaviour, statements,
              actions, or omissions of any user.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            5. Safety Disclaimer
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-2 mb-4 leading-relaxed"
          >
            The Platform does not conduct background checks or criminal
            verification. Hence, Users are advised to:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>Meet in public places</li>
            <li>Inform a trusted contact</li>
            <li>Prioritise personal safety</li>
          </motion.ul>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            The Platform shall not be responsible for harassment, injury,
            emotional distress, loss, or damage arising from user interactions.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            6. Venue Partnerships & Third-Party Locations
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-4"
          >
            <li>
              The Platform may suggest or coordinate meetings at partner or
              third-party venues.
            </li>
            <li>
              The Platform does not own, operate, manage, or control any venue.
            </li>
            <li>
              Venue partners are independent third parties and not agents or
              representatives of the Platform.
            </li>
          </motion.ul>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-4 mb-2 leading-relaxed"
          >
            The Platform is not responsible for:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>Venue services, pricing, staff conduct, hygiene, or safety</li>
            <li>Incidents occurring at or related to venue premises</li>
            <li>
              Any disputes related to venue services must be resolved directly
              with the venue.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            7. Payments, Refunds & Rescheduling
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>All payments must be made in advance.</li>
            <li>
              Payments are non-refundable once a match or introduction has been
              initiated.
            </li>
            <li>
              Rescheduling, if offered, is subject to availability and goodwill
              and is not guaranteed.
            </li>
            <li>
              No refunds shall be issued due to dissatisfaction with matches,
              no-shows, or outcomes.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            8. Cancellations & No-Shows
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>
              The Platform does not guarantee attendance of any participant.
            </li>
            <li>
              The Platform is not liable for cancellations, no-shows, or
              last-minute changes by users.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            9. No Guarantees or Warranties
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-4 leading-relaxed"
          >
            All services are provided on an "as-is" and "as-available" basis.
          </motion.p>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            The Platform disclaims all warranties, express or implied, including
            but not limited to compatibility, safety, success, or user
            behaviour.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            10. Limitation of Liability
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mt-2 mb-4 leading-relaxed"
          >
            To the maximum extent permitted under Indian law, the Platform, its
            founders, team members, affiliates, and partners shall not be liable
            for:
          </motion.p>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>Personal injury, harassment, emotional distress, or loss</li>
            <li>Direct, indirect, incidental, or consequential damages</li>
            <li>
              Disputes or conflicts between users or between users and venues
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            11. Privacy & Data Usage
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>
              User information is collected solely for facilitating
              introductions.
            </li>
            <li>
              Personal data shall not be shared without consent, except where
              legally required.
            </li>
            <li>
              Users consent to communication via WhatsApp, email, or phone for
              service-related purposes.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            12. Defamation, Feedback & Complaints
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>
              The Platform does not endorse statements or opinions of users.
            </li>
            <li>
              Complaints are handled internally and not shared verbatim with
              other users.
            </li>
            <li>
              The Platform shall not be liable for feedback, opinions, or
              allegations made by users.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            13. Intellectual Property
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            All content, branding, text, and materials on the Platform are the
            intellectual property of the Platform and may not be reproduced
            without permission.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            14. Termination of Access
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            The Platform reserves the right to suspend or terminate access
            without notice for violation of these Terms or misuse of the
            Platform.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            15. Governing Law & Jurisdiction
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-4 leading-relaxed"
          >
            These Terms shall be governed by and construed in accordance with
            the laws of India.
          </motion.p>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            All disputes shall be subject to the exclusive jurisdiction of
            courts in India.
          </motion.p>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            16. Marital Status Declaration, Misrepresentation & Good Faith
            Operation
          </motion.h3>
          <motion.ul
            variants={item}
            className="list-disc ml-6 space-y-2 text-foreground/70 mb-6"
          >
            <li>
              By submitting the registration form and using the Platform, the
              user expressly declares and represents that they are legally
              single, unmarried, and not in any legally binding marital
              relationship at the time of registration and participation.
            </li>
            <li>
              The Platform operates on the presumption that every individual who
              completes the form and participates in a blind date is legally
              single and eligible to engage in social introductions.
            </li>
            <li>
              If any participant is later discovered to be married, engaged, or
              otherwise in a committed relationship, such misrepresentation
              shall be deemed solely the responsibility of that participant.
            </li>
            <li>
              The Platform does not intentionally match users with married
              individuals, does not promote infidelity, and does not facilitate
              deception of any kind.
            </li>
          </motion.ul>

          <motion.h3
            variants={item}
            className="text-xl font-semibold mt-6 mb-3"
          >
            17. Acceptance of Terms
          </motion.h3>
          <motion.p
            variants={item}
            className="text-foreground/70 mb-6 leading-relaxed"
          >
            By using this Platform and selecting the checkbox stating "I agree
            to the Terms & Conditions", you confirm that you have read,
            understood, and accepted all the above terms.
          </motion.p>
        </section>

        {/* LAST UPDATED */}
        <motion.p variants={item} className="text-sm text-foreground/60 mt-10">
          Last Updated: {lastUpdated}
        </motion.p>
      </motion.div>
      {/* TERMS & CONDITIONS
      <section id="terms" className="mb-20">
        <motion.h2
          variants={item}
          className="text-3xl font-semibold text-primary mb-4"
        >
          Terms & Conditions
        </motion.h2>

        <motion.div
          variants={item}
          className="space-y-6 text-foreground/70 leading-relaxed"
        >
          <p>
            By accessing or using this website and our services you agree to be
            bound by the following Terms & Conditions.
          </p>

          <p>
            <strong>1. Nature of the Platform</strong>
            <br />
            The Platform operates as an introduction and facilitation service
            that connects individuals for social interactions. The Platform does
            not guarantee compatibility, chemistry, relationships, outcomes,
            safety, or conduct of any kind.
          </p>

          <p>
            <strong>2. Eligibility & User Representations</strong>
            <br />
            By using the Platform, you confirm that:
          </p>
          <ul className="list-disc ml-6">
            <li>You must be 18 years of age or older to use the Platform.</li>
            <li>All information provided is true and accurate</li>
            <li>You are legally capable of consenting</li>
            <li>
              The Platform reserves the right to reject, suspend, or remove any
              user at its discretion.
            </li>
          </ul>

          <p>
            <strong>3. Role of the Platform & Team</strong>
          </p>
          <ul className="list-disc ml-6">
            <li>The Platform facilitates introductions only.</li>
            <li>
              Any communication by team members is informational and
              facilitative in nature.
            </li>
            <li>
              No team member has authority to make guarantees beyond these
              Terms.
            </li>
          </ul>

          <p>
            <strong>4. User Responsibility & Assumption of Risk</strong>
          </p>
          <ul className="list-disc ml-6">
            <li>
              All meetings and communications occur at the user’s own risk.
            </li>
            <li>Users are solely responsible for their conduct.</li>
            <li>The Platform is not liable for behaviour of any user.</li>
          </ul>

          <p>
            <strong>5. Safety Disclaimer</strong>
            <br />
            The Platform does not conduct background checks. Users are advised
            to meet in public places and prioritise safety.
          </p>

          <p>
            <strong>6. Venue Partnerships & Third-Party Locations</strong>
            <br />
            The Platform may suggest third-party venues but is not responsible
            for venue services or incidents.
          </p>

          <p>
            <strong>7. Payments, Refunds & Rescheduling</strong>
          </p>
          <ul className="list-disc ml-6">
            <li>All payments must be made in advance.</li>
            <li>Payments are non-refundable once a match is initiated.</li>
            <li>Rescheduling is not guaranteed.</li>
          </ul>

          <p>
            <strong>8. Cancellations & No-Shows</strong>
            <br />
            The Platform is not liable for cancellations or no-shows by users.
          </p>

          <p>
            <strong>9. No Guarantees or Warranties</strong>
            <br />
            Services are provided “as-is” without warranties.
          </p>

          <p>
            <strong>10. Limitation of Liability</strong>
            <br />
            To the maximum extent permitted under Indian law, the Platform is
            not liable for damages, disputes, or losses.
          </p>

          <p>
            <strong>11. Privacy & Data Usage</strong>
            <br />
            User information is collected solely for facilitating introductions.
          </p>

          <p>
            <strong>12. Defamation, Feedback & Complaints</strong>
            <br />
            The Platform does not endorse opinions of users and is not liable
            for user feedback.
          </p>

          <p>
            <strong>13. Intellectual Property</strong>
            <br />
            All content and branding are the intellectual property of the
            Platform.
          </p>

          <p>
            <strong>14. Termination of Access</strong>
            <br />
            The Platform may suspend access for violations of these Terms.
          </p>

          <p>
            <strong>15. Governing Law & Jurisdiction</strong>
            <br />
            Governed by laws of India with jurisdiction in Indian courts.
          </p>

          <p>
            <strong>16. Marital Status Declaration</strong>
            <br />
            Users declare they are legally single. Misrepresentation is the
            user's responsibility.
          </p>

          <p>
            <strong>17. Acceptance of Terms</strong>
            <br />
            By using this Platform and agreeing to the checkbox, you accept all
            Terms.
          </p>
        </motion.div>
      </section>

      {/* PRIVACY POLICY */}
      {/* <section id="privacy" className="mb-20">
        <motion.h2
          variants={item}
          className="text-3xl font-semibold text-primary mb-4"
        >
          Privacy Policy
        </motion.h2>

        <motion.div
          variants={item}
          className="space-y-6 text-foreground/70 leading-relaxed"
        >
          <p>
            <strong>1. Information We Collect</strong>
          </p>
          <ul className="list-disc ml-6">
            <li>Name or preferred name</li>
            <li>Age confirmation</li>
            <li>Phone number and contact details</li>
            <li>Dating preferences</li>
            <li>Communication records</li>
            <li>Payment confirmation details</li>
          </ul>

          <p>
            <strong>2. Purpose of Data Collection</strong>
          </p>
          <ul className="list-disc ml-6">
            <li>Facilitate introductions</li>
            <li>Communicate regarding services</li>
            <li>Process payments</li>
            <li>Improve experience</li>
            <li>Address complaints</li>
          </ul>

          <p>
            <strong>3. Consent</strong>
            <br />
            By using the Platform, you consent to data collection and
            communication.
          </p>

          <p>
            <strong>4. Data Sharing & Disclosure</strong>
            <br />
            Data is shared only with consent, venue partners if required, or
            when legally necessary.
          </p>

          <p>
            <strong>5. Internal Access & Data Handling</strong>
            <br />
            Access is restricted to authorised team members.
          </p>

          <p>
            <strong>6. Data Storage & Security</strong>
            <br />
            We take reasonable steps to protect your data.
          </p>

          <p>
            <strong>7. Data Retention</strong>
            <br />
            Data is retained only as long as necessary.
          </p>

          <p>
            <strong>8. User Rights</strong>
            <br />
            Users may request access, correction, or deletion of data.
          </p>

          <p>
            <strong>9. Third-Party Links</strong>
            <br />
            We are not responsible for third-party privacy practices.
          </p>

          <p>
            <strong>10. Children’s Privacy</strong>
            <br />
            The Platform is for users 18+ only.
          </p>

          <p>
            <strong>11. Changes to This Policy</strong>
            <br />
            Continued use constitutes acceptance of updates.
          </p>

          <p>
            <strong>12. Contact</strong>
            <br />
            Contact us via the Platform for data-related requests.
          </p>
        </motion.div>
      </section> */}
    </main>
  );
};
